import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * HE TALKS, THE WORDS LAND IN THE CHAT BOX (VOICE1, size A, slice 2 - the 130th, 2026-10-11).
 *
 * His words: "can we add a voice to the AI chat? So we can talk to it". The device's own listener, no service.
 *
 * ⭐⭐ WHAT WAS MEASURED (`docs/planning/2026-10-10-voice1-probe/`: the iPhone simulator on iOS 26.0.1, as a Safari tab
 *    AND installed to the home screen):
 *      - the listener exists in a home-screen app under its prefixed name (`webkitSpeechRecognition`) only;
 *      - asked to start INSIDE A TAP, iOS shows its own prompt (Speech Recognition in Safari - "Speech data from this app
 *        will be sent to Apple" - the Microphone in the home-screen app), and the listener reports nothing while it is up;
 *      - a prompt refused, or never answered, gave NO error at all - only `end`.
 * ⛔ SO "IT NEVER STARTED" IS NOTICED HERE AND SAID IN A SENTENCE. A microphone button that does nothing is the dead
 *    button his rulings forbid; every way the device can decline ends as words in `problem`.
 * 🚨 CORRECTED THE 131ST, BY LOOKING (the same probe, buttons 5 to 8): that `end` was the probe's own stop() five
 *    seconds in. With nothing stopping it, the home-screen app's Microphone prompt answered NO gives
 *    `error: not-allowed` and NOTHING after it - no `end`, and stop() and abort() on that listener report nothing.
 *    The first build waited for an `end` to close: it sat on "Waiting for the microphone..." with a button no tap
 *    could move. ⛔ SO AN ERROR CLOSES THE LISTENING ITSELF; an `end` that does follow finds nothing left to do.
 *    Also measured: a second try in the same run of the app is refused at once, without asking; the app closed and
 *    opened again is asked again - which is what `NOT_ALLOWED` tells him to do.
 * ⛔ ONE UTTERANCE AT A TIME (`continuous = false`): the continuous setting is the one reported to hang on an iPhone.
 * ⭐ His words are never thrown away by the app: what the device finished AND the piece it had not finished yet are both
 *    handed over when it ends. Only `cancel` (and leaving the screen) discards.
 * ⚠️ NOT measured: a real voice becoming words. This decides what the app does with whatever the device reports.
 * ⭐ THE 132ND (slice 3, the back-and-forth mode): a listen that ended with NOTHING is told to the caller too
 *    (`onNothing`), with the one fact that separates "he said nothing" from "the phone will not listen" - whether it
 *    ever BEGAN - and the device's own code. What to do next is the mode's to decide, never this hook's.
 */

interface Heard { 0: { transcript: string }; isFinal: boolean }
interface Listener {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onresult: ((e: { results: ArrayLike<Heard> }) => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type ListenerMaker = new () => Listener;

const deviceListener = (): ListenerMaker | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: ListenerMaker; webkitSpeechRecognition?: ListenerMaker };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

const NEVER_STARTED =
  'Listening did not start. If the phone asked to use the microphone, allow it, then tap the microphone again.';
const NOT_ALLOWED =
  'The microphone is not allowed for this app. To be asked again, close the app completely and open it, then tap the '
  + "microphone. If it does not ask, allow the microphone in the phone's settings.";
const COULD_NOT_START = 'Listening could not start. Tap the microphone to try again.';

/** The device's own code as words for him. `null` is "nothing to say": silence, or a stop he asked for. */
const sentenceFor = (code: string): string | null => {
  switch (code) {
    case 'no-speech':
    case 'aborted':
      return null;
    case 'not-allowed':
    case 'service-not-allowed':
      return NOT_ALLOWED;
    case 'audio-capture':
      return 'No microphone was found.';
    case 'network':
      return 'Listening needs a connection, and it dropped. Tap the microphone to try again.';
    default:
      return `Listening stopped (${code}). Tap the microphone to try again.`;
  }
};

export interface Dictation {
  /** This browser has a listener at all. False hides the control. */
  supported: boolean;
  /** `waiting`: asked, and the device has not begun (its permission prompt may be up). */
  state: 'idle' | 'waiting' | 'listening';
  /** The words of this utterance so far - shown while he speaks. */
  hearing: string;
  /** Why it is not listening, in a sentence. Null when there is nothing to say. */
  problem: string | null;
  /** ⛔ CALL INSIDE THE TAP. */
  start: () => void;
  /** Finish now: what was heard is handed over when the device ends. */
  stop: () => void;
  /** Throw away what was heard. */
  cancel: () => void;
  /** Take the reason off the screen. For a try that was the app's own, whose refusal is not his to act on. */
  forget: () => void;
}

/** How a listen ended when it handed over no words: did the phone ever begin, and what code (if any) did it give. */
export interface NothingHeard { began: boolean; code: string | null }

export const useDictation = (options: {
  onFinal: (text: string) => void;
  /** It ended and there were no words. Not told for a listen that was cancelled - that one was thrown away. */
  onNothing?: (why: NothingHeard) => void;
}): Dictation => {
  const supported = deviceListener() !== null;
  const [state, setState] = useState<Dictation['state']>('idle');
  const [hearing, setHearing] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const listener = useRef<Listener | null>(null);
  const onFinal = useRef(options.onFinal);
  onFinal.current = options.onFinal;
  const onNothing = useRef(options.onNothing);
  onNothing.current = options.onNothing;

  const start = useCallback((): void => {
    const Maker = deviceListener();
    if (!Maker || listener.current) return;
    setProblem(null);
    setHearing('');

    const mine = new Maker();
    mine.lang = 'en-US';
    mine.interimResults = true;
    mine.continuous = false;
    let began = false;
    let words = '';
    let said: string | null | undefined;
    let code: string | null = null;

    mine.onstart = () => {
      if (listener.current !== mine) return;
      began = true;
      setState('listening');
    };
    mine.onresult = (e) => {
      if (listener.current !== mine) return;
      // Everything the device has for this utterance, finished pieces and the unfinished one alike.
      words = Array.from(e.results).map((r) => r[0].transcript).join('').replace(/\s+/g, ' ').trim();
      setHearing(words);
    };
    /** The listening is over, whichever of the device's two last words came first. Runs once per listener. */
    const close = (): void => {
      // A listener that was cancelled, already closed, or outlived its screen, hands over nothing.
      if (listener.current !== mine) return;
      listener.current = null;
      setState('idle');
      setHearing('');
      if (words) { onFinal.current(words); return; }
      // ⛔ It ended with no error, no start and no word. Said, never a dead button.
      if (!began && said === undefined) setProblem(NEVER_STARTED);
      onNothing.current?.({ began, code });
    };
    mine.onerror = (e) => {
      if (listener.current !== mine) return;
      code = e.error;
      said = sentenceFor(e.error);
      if (said) setProblem(said);
      // ⛔ MEASURED: after a refusal no `end` ever comes. The error is the end.
      close();
    };
    mine.onend = close;

    listener.current = mine;
    setState('waiting');
    try {
      // ⛔ Synchronously, in this call: the tap is what lets the phone listen.
      mine.start();
    } catch {
      listener.current = null;
      setState('idle');
      setProblem(COULD_NOT_START);
    }
  }, []);

  const stop = useCallback((): void => {
    try { listener.current?.stop(); } catch { /* it has already ended; `onend` says so */ }
  }, []);

  const cancel = useCallback((): void => {
    const mine = listener.current;
    if (!mine) return;
    listener.current = null;
    setState('idle');
    setHearing('');
    try { mine.abort(); } catch { /* already gone */ }
  }, []);

  // Leaving the screen stops the microphone, and nothing heard after that is handed anywhere.
  useEffect(() => () => {
    const mine = listener.current;
    listener.current = null;
    try { mine?.abort(); } catch { /* already gone */ }
  }, []);

  const forget = useCallback((): void => setProblem(null), []);

  return { supported, state, hearing, problem, start, stop, cancel, forget };
};
